/* 요소 그리기 — 편집 화면 · 템플릿 미리보기 · 페이지 목록 썸네일이 같이 쓴다. 데이터(요소 하나) → 모양.
   공개 보기 페이지(Portfolio resources/showcase/viewer-template.html)도 같은 규칙으로 그린다. */
import { memo, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { DEFAULT_TEXT_COLOR, PHOTO_CLIPS, SHAPES } from '../model/constants';
import { useImageUrl } from '../model/images';
import { PAGE_H, PAGE_W, type El, type Page } from '../model/types';

export function shapeIcon(kind: string, fill?: string): ReactNode {
  const f = fill || 'currentColor';
  if (kind === 'rect') return <svg viewBox="0 0 100 100"><rect x="6" y="14" width="88" height="72" fill={f} /></svg>;
  if (kind === 'round') return <svg viewBox="0 0 100 100"><rect x="6" y="14" width="88" height="72" rx="18" fill={f} /></svg>;
  if (kind === 'circle') return <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="44" fill={f} /></svg>;
  if (kind === 'line') return <svg viewBox="0 0 100 100"><line x1="6" y1="50" x2="94" y2="50" stroke={f} strokeWidth="7" /></svg>;
  return <svg viewBox="-6 -6 112 112"><path d={SHAPES[kind]?.d} fill={f} /></svg>;
}

function LineSvg({ elm }: { elm: El }) {
  const w = Math.max(10, elm.w), h = Math.max(10, elm.h), sw = elm.sw || 3, c = elm.stroke || elm.bg || '#1F2937'; // dq-ignore: 문서 요소 기본 색 — 공개 페이지(viewer-template.html)·exporter 와 같아야 함
  const y = h / 2, head = Math.max(10, sw * 3.2), a = elm.arrow || 'none';
  const x1 = a === 'both' ? head : sw / 2, x2 = a === 'end' || a === 'both' ? w - head : w - sw / 2;
  const dash = elm.dash === 'dashed' ? { strokeDasharray: `${sw * 3} ${sw * 2}` } : elm.dash === 'dotted' ? { strokeDasharray: `0 ${sw * 2}`, strokeLinecap: 'round' as const } : {};
  const tri = (x: number, dir: number) => <path d={`M${x} ${y - head * 0.6} L${x + dir * head} ${y} L${x} ${y + head * 0.6} Z`} fill={c} />;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%" className="block overflow-visible">
      <line x1={x1} y1={y} x2={x2} y2={y} stroke={c} strokeWidth={sw} {...dash} />
      {(a === 'end' || a === 'both') && tri(w - head, 1)}
      {a === 'both' && tri(head, -1)}
    </svg>
  );
}

/** 요소 안쪽(모양) 스타일 — 위치·크기·회전은 바깥 상자 */
export function bodyStyle(elm: El, imgUrl?: string | null): CSSProperties {
  const s: CSSProperties = {};
  if (elm.type === 'image') {
    if (imgUrl) s.backgroundImage = `url('${imgUrl}')`;
  } else if (elm.type === 'shape') {
    const kind = elm.kind || 'rect';
    if (kind === 'rect' || kind === 'round' || kind === 'circle') {
      s.background = elm.bg;
      s.borderRadius = kind === 'circle' ? '50%' : kind === 'round' ? `${elm.radius ?? 24}px` : `${elm.radius || 0}px`;
      if (elm.sw) s.boxShadow = `inset 0 0 0 ${elm.sw}px ${elm.stroke || '#111827'}`; // dq-ignore: 문서 요소 기본 색 — 공개 페이지(viewer-template.html)·exporter 와 같아야 함
    } else s.background = 'transparent';
  } else {
    s.fontSize = `${elm.fontSize || 16}px`;
    s.fontWeight = elm.bold ? 800 : 500;
    s.textAlign = elm.align || 'left';
    s.color = elm.color || DEFAULT_TEXT_COLOR;
    if (elm.font) s.fontFamily = elm.font;
    if (elm.ls) s.letterSpacing = elm.ls;
    if (elm.lh) s.lineHeight = String(elm.lh);
    if (elm.italic) s.fontStyle = 'italic';
    if (elm.underline) s.textDecoration = 'underline';
    if (elm.bg) { s.background = elm.bg; s.padding = elm.pad || '8px 14px'; }
    if (elm.vcenter) {
      s.display = 'flex'; s.alignItems = 'center';
      s.justifyContent = elm.align === 'center' ? 'center' : elm.align === 'right' ? 'flex-end' : 'flex-start';
    }
  }
  if (elm.radius && elm.type !== 'shape') s.borderRadius = `${elm.radius}px`;
  if (elm.opacity != null && elm.opacity < 1) s.opacity = elm.opacity;
  if (elm.flipX || elm.flipY) s.transform = `scale(${elm.flipX ? -1 : 1}, ${elm.flipY ? -1 : 1})`;
  if (elm.type === 'image' && elm.clip && elm.clip !== 'none') {
    if (elm.clip === 'round') s.borderRadius = `${elm.radius || 28}px`;
    else if (elm.clip === 'arch') {
      const r = Math.min(elm.w / 2, elm.h);
      s.borderRadius = `${elm.w / 2}px ${elm.w / 2}px 0 0 / ${r}px ${r}px 0 0`;
    } else s.clipPath = PHOTO_CLIPS[elm.clip]?.css;
  }
  return s;
}
/* 요소 안쪽 클래스 — el-* 이름은 공개 보기 페이지(viewer-template.html)와 같게 두고(글 상자 찾기에도 씀), 모양은 토큰 유틸로.
   글 상자 안쪽 여백 4px(spacing-02)·줄 간격 1.45 는 공개 페이지·사진 내보내기와 같아야 줄바꿈이 같다 */
export const bodyClass = (elm: El, still = false) => 'el-body size-full ' + (elm.type === 'image' ? 'el-image-body bg-[var(--gray-300)] bg-cover bg-center'
  : elm.type === 'shape' ? 'el-shape-body' : 'el-text-body overflow-hidden whitespace-pre-wrap break-words p-02 leading-[1.45] outline-none ' + (still ? 'cursor-default' : 'cursor-text'));

/** 도형 안쪽 그림(SVG 도형·선) */
export function ShapeInner({ elm }: { elm: El }) {
  const kind = elm.kind || 'rect';
  if (kind === 'line') return <LineSvg elm={elm} />;
  if (kind === 'rect' || kind === 'round' || kind === 'circle') return null;
  const sw = elm.sw || 0;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="block size-full overflow-visible">
      <path d={SHAPES[kind]?.d} fill={elm.bg}
        {...(sw ? { stroke: elm.stroke || '#111827', strokeWidth: sw, vectorEffect: 'non-scaling-stroke', strokeLinejoin: 'round' as const } : {})} /> {/* dq-ignore: 문서 요소 기본 색 — 공개 페이지(viewer-template.html)·exporter 와 같아야 함 */}
    </svg>
  );
}

/** 정적 요소 (미리보기·썸네일) */
function StaticEl({ elm, thumb }: { elm: El; thumb: boolean }) {
  const url = useImageUrl(elm.type === 'image' ? elm.src : undefined, thumb);
  return (
    <div className="page-el absolute" style={outerStyle(elm)}>
      <div className={bodyClass(elm, true)} style={bodyStyle(elm, url)}>
        {elm.type === 'shape' && <ShapeInner elm={elm} />}
        {elm.type === 'text' && elm.text}
      </div>
    </div>
  );
}
export function outerStyle(elm: El): CSSProperties {
  return { left: elm.x, top: elm.y, width: elm.w, height: elm.h, zIndex: elm.z, transform: elm.rot ? `rotate(${elm.rot}deg)` : undefined };
}

/** 720×960 페이지를 width 폭으로 축소해서 그대로 그린다. width="fill" 이면 놓인 칸 폭을 재서 꽉 채운다(창 크기 따라) */
export function PageThumb({ page, width, className }: { page: Page | null | undefined; width: number | 'fill'; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(0);
  const fill = width === 'fill';
  useLayoutEffect(() => {
    if (!fill || !box.current) return;
    const el = box.current;
    const ro = new ResizeObserver(() => setMeasured(el.clientWidth));
    ro.observe(el);
    setMeasured(el.clientWidth);
    return () => ro.disconnect();
  }, [fill]);
  const w = fill ? measured : width;
  const s = w / PAGE_W;
  return (
    <div ref={box} className={'page-thumb relative overflow-hidden bg-[var(--gray-0)] ' + (fill ? 'aspect-[3/4] w-full ' : '') + (className || '')}
      style={fill ? undefined : { width: w, height: Math.round(PAGE_H * s) }}>
      {page && w > 0 && (
        <div className="pointer-events-none absolute left-0 top-0 origin-top-left" style={{ width: PAGE_W, height: PAGE_H, background: page.bg || '#FFFFFF', transform: `scale(${s})` }}> {/* dq-ignore: 문서 요소 기본 색 — 공개 페이지(viewer-template.html)·exporter 와 같아야 함 */}
          {page.elements.slice().sort((a, b) => a.z - b.z).map((e) => <StaticEl key={e.id} elm={e} thumb={w < 400} />)}
        </div>
      )}
    </div>
  );
}

/** 문서를 직접 고치므로 tick(바뀐 시점)이 바뀔 때만 다시 그리는 썸네일 */
export const PageThumbMemo = memo(function PageThumbMemo(p: { page: Page; width: number; tick: number; className?: string }) {
  return <PageThumb page={p.page} width={p.width} className={p.className} />;
});
